import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { Search, ShieldQuestion } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import Card from '../../../components/Card'
import BannerImage from '../../../components/BannerImage'
import RealmSelect from '../../../components/RealmSelect'
import useReferenceStore from '../../../state/useReferenceStore'
import { prepareOnboardingGuild } from '../../../api/search'
import { createManualGuild } from '../../../api/guild'
import { useGameVersion } from '../../../hooks/useGameVersion'
import { awaitPendingJob, displayErrorToast } from '../../../helpers'

const GuildSearch = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()
  const { storeReferences } = useReferenceStore()

  const [name, setName] = useState('')
  const [realm, setRealm] = useState<wowaudit.Realm>()
  const gameVersion = useGameVersion()
  const hasBlizzardApi = gameVersion.has('blizzard_api')
  const [searching, setSearching] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [preparingGuild, setPreparingGuild] =
    useState<wowaudit.GuildReference>()

  const canSubmit = name.trim().length > 1 && !!realm && !searching

  const handleManualSubmit = async (selectedRealm: wowaudit.Realm) => {
    setSearching(true)

    try {
      const guild = await createManualGuild(selectedRealm.id, name.trim())
      storeReferences([guild])
      navigate(`${guild.path}/onboarding`)
    } catch (error) {
      displayErrorToast(error as Error, t)
      setSearching(false)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit || !realm) return
    if (!hasBlizzardApi) return handleManualSubmit(realm)

    setSearching(true)
    setNotFound(false)

    try {
      const { guild, job_id: jobId } = await prepareOnboardingGuild(
        name.trim(),
        realm,
      )

      if (!guild || !jobId) {
        setNotFound(true)
        setSearching(false)
        return
      }

      storeReferences([guild])
      setPreparingGuild(guild)

      awaitPendingJob(jobId, (result) => {
        if (result.error) {
          displayErrorToast(result.error, t)
          setSearching(false)
          setPreparingGuild(undefined)
          return
        }

        navigate(`${guild.path}/onboarding`)
      })
    } catch (error) {
      displayErrorToast(error as Error, t)
      setSearching(false)
      setPreparingGuild(undefined)
    }
  }

  if (preparingGuild) {
    return (
      <motion.div
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className='mx-auto flex max-w-md flex-col items-center gap-6 py-16 text-center'
      >
        <BannerImage
          size='large'
          faction={preparingGuild.faction}
          fileName={preparingGuild.bannerFilename}
          className='rounded-md'
        />
        <div className='flex flex-col items-center gap-2'>
          <h1 className='text-2xl font-semibold'>
            Looking for {preparingGuild.name}
          </h1>
          <p className='text-muted-foreground'>
            We&apos;re fetching the latest guild details from Blizzard&apos;s
            API. This only takes a moment.
          </p>
        </div>
        <Progress className='h-1 w-48' indeterminate />
      </motion.div>
    )
  }

  return (
    <div className='mx-auto flex max-w-2xl flex-col items-center gap-8 py-12 text-center'>
      <motion.div
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className='flex flex-col items-center gap-3'
      >
        <span className='flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary'>
          <ShieldQuestion className='h-7 w-7' />
        </span>
        <h1 className='text-3xl font-semibold max-sm:text-2xl'>
          Let&apos;s find your guild
        </h1>
        <p className='max-w-md text-muted-foreground'>
          {hasBlizzardApi
            ? 'Search for your guild to start setting up your team.'
            : 'Choose your region and ruleset, then enter the name of your guild to start setting up your team.'}
        </p>
      </motion.div>

      <motion.div
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
        className='w-full'
      >
        <Card className='flex flex-col gap-3 p-4 text-left sm:flex-row sm:items-center'>
          <RealmSelect
            realm={realm}
            setRealm={setRealm}
            className='w-full sm:w-100'
          />
          <Input
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              setNotFound(false)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSubmit()
            }}
            placeholder='Guild name'
            className='grow'
          />
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={searching}
            spinner={hasBlizzardApi ? 'blizzard' : undefined}
            className='gap-2'
          >
            <Search className='h-4 w-4' />
            {hasBlizzardApi ? 'Search' : 'Continue'}
          </Button>
        </Card>
      </motion.div>

      {notFound ? (
        <Alert variant='warning' className='text-left'>
          <AlertTitle>We couldn&apos;t find that guild</AlertTitle>
          <AlertDescription>
            Double-check the exact guild name and realm, then try again. If your
            guild is new, it may take a few days to appear in Blizzard&apos;s
            API.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

export default GuildSearch
